// packages/shared/src/widget-theme.ts
//
// The widget appearance options the API validates, the dashboard offers and
// the widget applies. Kept in one place because a font the dashboard lists but
// the widget cannot load would save fine and then silently render as system-ui.

/**
 * Fonts a clinic may choose, all on Google Fonts. A short, safe list rather
 * than a free-text field: an arbitrary family name cannot be loaded, and a
 * decorative display face makes the consent text hard to read.
 */
export const WIDGET_FONTS = [
  { family: 'Inter', style: 'Neutral, very readable' },
  { family: 'DM Sans', style: 'Soft and modern' },
  { family: 'Poppins', style: 'Round and friendly' },
  { family: 'Manrope', style: 'Clean, slightly technical' },
  { family: 'Nunito Sans', style: 'Warm and gentle' },
  { family: 'Montserrat', style: 'Bold and geometric' },
  { family: 'Lato', style: 'Classic and calm' },
  { family: 'Lora', style: 'Elegant serif' },
] as const;

export type WidgetFont = (typeof WIDGET_FONTS)[number]['family'];

export const WIDGET_FONT_FAMILIES = WIDGET_FONTS.map((f) => f.family) as unknown as readonly [
  WidgetFont,
  ...WidgetFont[],
];

/** The stylesheet for one font, in the weights the widget's CSS uses. */
export function googleFontHref(family: string): string {
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
}

export const WIDGET_BACKGROUND_STYLES = ['plain', 'tinted'] as const;
export type WidgetBackgroundStyle = (typeof WIDGET_BACKGROUND_STYLES)[number];

export const LAUNCHER_POSITIONS = ['bottom-left', 'bottom-right'] as const;
export type LauncherPosition = (typeof LAUNCHER_POSITIONS)[number];

export const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function channels(hex: string): [number, number, number] | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return null;
  return [parseInt(match[1]!, 16), parseInt(match[2]!, 16), parseInt(match[3]!, 16)];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number | null {
  const rgb = channels(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The text colour the widget puts on a brand-coloured button when the clinic
 * has not chosen one. The dashboard shows this same answer, so what staff see
 * while picking is what patients get.
 */
export function readableTextOn(hex: string): '#111111' | '#ffffff' {
  const luminance = relativeLuminance(hex);
  if (luminance === null) return '#ffffff';
  return luminance > 0.45 ? '#111111' : '#ffffff';
}
